import React, { useEffect, useRef, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import AddCommentIcon from "@material-ui/icons/AddComment";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { makeStyles } from "@material-ui/core/styles";
import { sendAssistantMessage, fetchTodayConversation, resetConversation } from "../../services/assistantService";
import AssistantToolConfirmCard from "./AssistantToolConfirmCard";
import AssistantOptionsCard from "./AssistantOptionsCard";
import RemyMark from "./RemyMark";
import UseUserState from "../hooks/useUserState";

// Scoped dark surface for the assistant panel. Explicit tokens (not the
// global MUI theme) so the panel reads correctly regardless of the
// app-wide `.MuiDrawer-paper` override in styles/customs.css.
const COLORS = {
  panelBg: "#14181d",
  surface: "#1c2129",
  surfaceRaised: "#232a33",
  border: "rgba(255,255,255,0.08)",
  accent: "#2f8fff",
  accentSoft: "rgba(47,143,255,0.14)",
  userBubbleFrom: "#2f8fff",
  userBubbleTo: "#076cd8",
  textPrimary: "#edeff2",
  textSecondary: "#8d99a8",
  danger: "#ff6b6b",
  online: "#34d399",
};

const useStyles = makeStyles(() => ({
  panel: {
    width: 380,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    // Without minHeight:0, a flex column's height:100% doesn't cap its
    // children — messageList's flex:1 + overflow-y:auto below has nothing
    // to actually overflow *against*, so the whole panel just grows past
    // the viewport instead of scrolling internally (MuiDrawer-paper itself
    // sets overflow-y:auto too, so the outer paper silently becomes the
    // only scroll container, and content — like a confirm/options card —
    // can end up unreachable below the fold).
    minHeight: 0,
    backgroundColor: COLORS.panelBg,
    color: COLORS.textPrimary,
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    borderBottom: `1px solid ${COLORS.border}`,
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerTitleGroup: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.25,
  },
  headerName: {
    fontSize: 15,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  headerSubtitle: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    color: COLORS.textSecondary,
  },
  onlineDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: COLORS.online,
    boxShadow: `0 0 0 3px rgba(52,211,153,0.18)`,
  },
  closeButton: {
    color: COLORS.textSecondary,
    "&:hover": { color: COLORS.textPrimary, backgroundColor: "rgba(255,255,255,0.06)" },
  },
  messageList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "16px",
  },
  bubbleRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
  },
  bubbleRowUser: {
    justifyContent: "flex-end",
  },
  bubbleRowAssistant: {
    justifyContent: "flex-start",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${COLORS.userBubbleFrom}, ${COLORS.userBubbleTo})`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  bubble: {
    maxWidth: "78%",
    padding: "9px 13px",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: "break-word",
    "& p": { margin: 0 },
    "& p + p": { marginTop: 6 },
    "& ul, & ol": { margin: "4px 0", paddingLeft: 20 },
    "& strong": { fontWeight: 600 },
    "& table": {
      borderCollapse: "collapse",
      margin: "6px 0",
      fontSize: 12,
      display: "block",
      overflowX: "auto",
      maxWidth: "100%",
    },
    "& th, & td": {
      border: `1px solid ${COLORS.border}`,
      padding: "4px 8px",
      textAlign: "left",
      whiteSpace: "nowrap",
    },
    "& th": {
      backgroundColor: "rgba(255,255,255,0.06)",
      fontWeight: 600,
    },
  },
  bubbleUser: {
    background: `linear-gradient(135deg, ${COLORS.userBubbleFrom}, ${COLORS.userBubbleTo})`,
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  bubbleAssistant: {
    backgroundColor: COLORS.surface,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.border}`,
    borderBottomLeftRadius: 4,
  },
  bubbleError: {
    backgroundColor: "rgba(255,107,107,0.08)",
    color: COLORS.danger,
    border: `1px solid rgba(255,107,107,0.35)`,
    borderBottomLeftRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  "@keyframes typingBounce": {
    "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
    "40%": { transform: "scale(1)", opacity: 1 },
  },
  typingRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  typingLabel: {
    fontSize: 12,
    color: COLORS.textSecondary,
  },
  typingDots: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 0",
  },
  typingDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: COLORS.accent,
    animation: "$typingBounce 1.2s infinite ease-in-out",
  },
  quickActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  chip: {
    backgroundColor: "transparent",
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textPrimary,
    "&:hover": { backgroundColor: COLORS.accentSoft, borderColor: COLORS.accent },
    "& .MuiChip-label": { paddingLeft: 10, paddingRight: 10 },
  },
  pendingBanner: {
    flexShrink: 0,
    margin: "0 16px 8px",
    padding: "8px 12px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    textAlign: "center",
    cursor: "pointer",
    color: COLORS.textPrimary,
    backgroundColor: COLORS.accentSoft,
    border: `1px solid ${COLORS.accent}`,
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    borderTop: `1px solid ${COLORS.border}`,
    flexShrink: 0,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.surface,
      borderRadius: 22,
      color: COLORS.textPrimary,
      fontSize: 14,
      "& fieldset": { borderColor: COLORS.border },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.18)" },
      "&.Mui-focused fieldset": { borderColor: COLORS.accent },
    },
    "& .MuiOutlinedInput-input": { padding: "10px 16px" },
    "& .MuiOutlinedInput-input::placeholder": { color: COLORS.textSecondary, opacity: 1 },
  },
  sendButton: {
    backgroundColor: COLORS.accent,
    color: "#fff",
    padding: 9,
    "&:hover": { backgroundColor: "#1c7ef0" },
    "&.Mui-disabled": { backgroundColor: COLORS.surface, color: COLORS.textSecondary },
  },
}));

const WRITE_TOOLS = ["createAppointment", "rescheduleAppointment", "blockDayUnavailable"];

const QUICK_ACTIONS = [
  { label: "📅 Crear una cita", prompt: "Quiero crear una nueva cita" },
  { label: "🗓️ Ver mis citas de esta semana", prompt: "¿Qué citas tengo esta semana?" },
  { label: "👥 Ver mis pacientes", prompt: "Muéstrame mi lista de pacientes" },
  { label: "🚫 Bloquear un día", prompt: "Necesito bloquear un día de mi agenda" },
];

const GREETING_MESSAGE = {
  role: "assistant",
  content: "¡Hola! Soy **Remy**, tu asistente médico. Puedo ayudarte a gestionar tus citas, pacientes y disponibilidad. ¿En qué te ayudo hoy?",
  __greeting: true,
};

function normalizeContent(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .filter((part) => part?.type === "text")
      .map((part) => part.text)
      .join("");
  }
  return "";
}

// Backend appends a technical marker (e.g. "[[PENDING:createAppointment]]")
// to proposal messages so it can force the right tool call once the doctor
// confirms — that marker must never be shown to the user.
const PENDING_MARKER_RE = /\s*\[\[PENDING:\w+\]\]\s*$/;

function MessageBubble({ role, content, isError }) {
  const classes = useStyles();
  const isUser = role === "user";
  const bubbleClass = isError ? classes.bubbleError : isUser ? classes.bubbleUser : classes.bubbleAssistant;
  const displayContent = isUser ? content : content.replace(PENDING_MARKER_RE, "");
  return (
    <div className={`${classes.bubbleRow} ${isUser ? classes.bubbleRowUser : classes.bubbleRowAssistant}`}>
       {!isUser && (
         <div className={classes.avatar}>
           <RemyMark pulseColor="#076cd8" size={18} />
         </div>
       )}
      <div className={`${classes.bubble} ${bubbleClass}`}>
        {isError && "⚠️ "}
        {isUser || isError ? (
          displayContent
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  const classes = useStyles();
  return (
    <div className={`${classes.bubbleRow} ${classes.bubbleRowAssistant}`}>
      <div className={classes.avatar}>
        <RemyMark pulseColor="#076cd8" size={18} />
      </div>
      <div className={`${classes.bubble} ${classes.bubbleAssistant} ${classes.typingRow}`}>
        <span className={classes.typingLabel}>Remy está escribiendo</span>
        <span className={classes.typingDots}>
          <span className={classes.typingDot} style={{ animationDelay: "0s" }} />
          <span className={classes.typingDot} style={{ animationDelay: "0.15s" }} />
          <span className={classes.typingDot} style={{ animationDelay: "0.3s" }} />
        </span>
      </div>
    </div>
  );
}

export default function AiChat({ open, onClose }) {
  const userState = UseUserState();
  const jwt = userState[0]?.jwt;
  const classes = useStyles();
  const listEndRef = useRef(null);
  const listRef = useRef(null);
  // Streaming state held in refs instead of React state so each text-delta
  // doesn't re-allocate the full `messages` array. The previous
  // implementation spread + filter on every delta (O(messages.length) per
  // delta, multiplied by the per-token delta rate — quadratic over the
  // turn). Now: first visible delta appends a streaming bubble; every
  // subsequent delta replaces just that bubble by index.
  const streamingIdRef = useRef(null);
  const assistantTextRef = useRef("");
  const hadVisibleOutputRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pendingToolCall, setPendingToolCall] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [atBottom, setAtBottom] = useState(true);

  useEffect(() => {
    if (open && jwt) {
      fetchTodayConversation(jwt).then((data) => {
        const normalized = (data.messages || []).map((m) => ({
          ...m,
          content: normalizeContent(m.content),
        }));
        if (normalized.length === 0) {
          setMessages([GREETING_MESSAGE]);
          setShowQuickActions(true);
        } else {
          setMessages(normalized);
          setShowQuickActions(false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll instantly, not smoothly: every streamed text-delta re-runs this
  // effect, and a smooth scroll started while the previous one is still
  // animating gets dropped by the browser. The confirm/options card mounts
  // right after the last delta, so it landed below the fold with the list
  // parked mid-way -- the doctor saw the assistant "stop" mid-task with the
  // pending-action card sitting off-screen. Also re-scroll on the next frame
  // so the card is measured at its final height before we scroll to it.
  useEffect(() => {
    const scrollToEnd = () => listEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    scrollToEnd();
    const frame = requestAnimationFrame(scrollToEnd);
    return () => cancelAnimationFrame(frame);
  }, [messages, isTyping, pendingToolCall, pendingSelection]);

  // Safety net for the same failure: if a pending card ends up off-screen
  // anyway (doctor scrolled up mid-stream, layout shift), an action waiting
  // on a click must never be silently invisible.
  function isListAtBottom() {
    const list = listRef.current;
    if (!list) return true;
    return list.scrollHeight - list.scrollTop - list.clientHeight < 24;
  }

  function handleListScroll() {
    setAtBottom(isListAtBottom());
  }

  useEffect(() => {
    setAtBottom(isListAtBottom());
  }, [messages, pendingToolCall, pendingSelection]);

  const hasHiddenPendingCard = Boolean(pendingToolCall || pendingSelection) && !atBottom;

  function sendMessage(text) {
    if (!text.trim()) return;
    setShowQuickActions(false);
    setPendingSelection(null);
    const userMessage = { role: "user", content: text };
    // Only user/assistant turns are valid wire history for the model API —
    // "tool" placeholder entries (used here just to trigger the confirm
    // card) carry no reconstructable content and internal UI flags like
    // __streaming/__greeting must not leak into the request payload.
    const priorMessages = messages
      .filter((m) => (m.role === "user" || m.role === "assistant") && !m.__greeting && !m.__error)
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    // Reset streaming state on every new turn. assistantTextRef and
    // hadVisibleOutputRef are mutated inside the callbacks below without
    // triggering re-renders — the streaming bubble's content is the only
    // thing that needs to flow back into React state, and that goes
    // through setMessages with a fixed-index in-place update.
    assistantTextRef.current = "";
    hadVisibleOutputRef.current = false;
    streamingIdRef.current = null;

    sendAssistantMessage({
      messages: [...priorMessages, userMessage],
      jwt,
      onTextChunk: (chunk) => {
        assistantTextRef.current += chunk;
        // Models sometimes emit whitespace-only text-deltas as step
        // boundaries around a tool call. Rendering those produces a blank
        // bubble sitting out of sequence with the real reply — skip until
        // there's actual visible content.
        if (!assistantTextRef.current.trim()) return;
        hadVisibleOutputRef.current = true;
        setIsTyping(false);
        setMessages((prev) => {
          const id = streamingIdRef.current;
          if (id == null) {
            // First visible delta: append a new streaming bubble and
            // remember its index for subsequent deltas.
            const newId = prev.length;
            streamingIdRef.current = newId;
            return [
              ...prev,
              { role: "assistant", content: assistantTextRef.current, __streaming: true },
            ];
          }
          // Subsequent deltas: replace just the streaming bubble in place.
          // `.map` allocates one new array but the per-element cost is O(1)
          // for non-streaming messages and O(1) for the streaming one.
          return prev.map((m, i) =>
            i === id ? { ...m, content: assistantTextRef.current } : m
          );
        });
      },
      onToolResult: ({ output }) => {
        setIsTyping(false);
        if (output?.status === "pending_confirmation" && WRITE_TOOLS.includes(output.toolName)) {
          hadVisibleOutputRef.current = true;
          setPendingToolCall({ toolName: output.toolName, args: output.args });
        } else if (output?.status === "needs_selection") {
          hadVisibleOutputRef.current = true;
          setPendingSelection({ field: output.field, prompt: output.prompt, options: output.options });
        }
      },
      onDone: () => {
        setIsTyping(false);
        streamingIdRef.current = null;
        setMessages((prev) => prev.map((m) => ({ ...m, __streaming: false })));
        // Belt-and-suspenders: some stream event types (or a stream that
        // silently ends early) can leave a turn with nothing to show —
        // typing indicator would just vanish with no explanation.
        if (!hadVisibleOutputRef.current) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "No pude generar una respuesta para eso. Intenta reformular tu mensaje.",
              __error: true,
            },
          ]);
        }
      },
      onError: (error) => {
        hadVisibleOutputRef.current = true;
        streamingIdRef.current = null;
        setIsTyping(false);
        console.error("Assistant error:", error);
        setMessages((prev) => [
          ...prev.filter((m) => !m.__streaming),
          {
            role: "assistant",
            content: error.message || "Ocurrió un error al contactar al asistente. Intenta de nuevo.",
            __error: true,
          },
        ]);
      },
    });
  }

  function handleSend() {
    sendMessage(input);
  }

  function handleQuickAction(prompt) {
    sendMessage(prompt);
  }

  function handleToolConfirmed(result) {
    setPendingToolCall(null);
    // Backend's createAppointmentCore returns { appointment, affiliationMessage }.
    // When the patient has an active affiliation but the plan does not cover
    // the booking, affiliationMessage is a non-empty warning the doctor needs
    // to see ("Condicion de Pago…"). Surface it in the assistant bubble
    // instead of always showing the same canned success line.
    const note = result?.data?.affiliationMessage
      ? `\n\n_${result.data.affiliationMessage}_`
      : "";
    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: `✅ Acción confirmada y ejecutada.${note}` },
    ]);
  }

  function handleToolCancel() {
    setPendingToolCall(null);
  }

  // Delete first, reset second. If the delete fails the thread is still alive
  // on the server, so showing a cleared panel would be a lie about server
  // state. Clearing pendingToolCall/pendingSelection is part of the contract:
  // a surviving confirm card would execute an action proposed in a thread
  // that no longer exists.
  async function handleNewConversation() {
    try {
      await resetConversation(jwt);
    } catch (error) {
      console.error("Assistant reset error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: error.message || "No se pudo iniciar una conversación nueva. Intenta de nuevo.",
          __error: true,
        },
      ]);
      return;
    }
    setMessages([GREETING_MESSAGE]);
    setShowQuickActions(true);
    setInput("");
    setPendingToolCall(null);
    setPendingSelection(null);
    setIsTyping(false);
  }

  function handleOptionSelected(option) {
    const field = pendingSelection.field;
    setPendingSelection(null);
    sendMessage(`Selecciono: ${option.label} (${field}=${option.value})`);
  }

  if (!open) return null;

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
           <div className={classes.avatar}>
             <RemyMark pulseColor="#076cd8" size={18} />
           </div>
          <div className={classes.headerTitleGroup}>
            <span className={classes.headerName}>Remy</span>
            <span className={classes.headerSubtitle}>
              <span className={classes.onlineDot} />
              Tu asistente médico
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Nueva conversación">
            <span>
              <IconButton
                onClick={handleNewConversation}
                disabled={isTyping}
                size="small"
                className={classes.closeButton}
              >
                <AddCommentIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} size="small" className={classes.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      <div className={classes.messageList} ref={listRef} onScroll={handleListScroll}>
        {messages
          .filter(
            (m) =>
              (m.role === "user" || m.role === "assistant") &&
              m.content?.replace(PENDING_MARKER_RE, "").trim()
          )
          .map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} isError={m.__error} />
          ))}
        {isTyping && <TypingIndicator />}
        {showQuickActions && (
          <div className={classes.quickActions}>
            {QUICK_ACTIONS.map((action) => (
              <Chip
                key={action.label}
                label={action.label}
                onClick={() => handleQuickAction(action.prompt)}
                clickable
                size="small"
                className={classes.chip}
              />
            ))}
          </div>
        )}
        {pendingSelection && (
          <AssistantOptionsCard
            field={pendingSelection.field}
            prompt={pendingSelection.prompt}
            options={pendingSelection.options}
            onSelect={handleOptionSelected}
          />
        )}
        {pendingToolCall && (
          <AssistantToolConfirmCard
            toolName={pendingToolCall.toolName}
            args={pendingToolCall.args}
            jwt={jwt}
            onConfirmed={handleToolConfirmed}
            onCancel={handleToolCancel}
          />
        )}
        <div ref={listEndRef} />
      </div>
      {hasHiddenPendingCard && (
        <div
          className={classes.pendingBanner}
          onClick={() => listEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" })}
        >
          ⬇ Hay una acción esperando tu confirmación
        </div>
      )}
      <div className={classes.inputRow}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Escribe un mensaje..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          className={classes.textField}
        />
        <IconButton onClick={handleSend} disabled={!input.trim()} className={classes.sendButton}>
          <SendIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
}
